import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

export interface Steuersatz {
  steuersatz_id: number;
  bezeichnung: string;
  prozent: number;
  ist_standard: boolean;
  aktiv: boolean;
}

/**
 * Pflege der Steuersatz-Stammdaten. Die Sätze werden in der Artikelmaske als
 * Dropdown angeboten; der Prozentwert eines Satzes propagiert bei Änderung
 * auf alle verknüpften Artikel (Belege behalten ihren eingefrorenen Satz).
 */
const SteuersatzSection: React.FC = () => {
  const [saetze, setSaetze] = useState<Steuersatz[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ bezeichnung: '', prozent: '' });

  const load = useCallback(() => {
    setLoading(true);
    setFehler(null);
    axios.get<Steuersatz[]>(`${API_URL}/api/steuersaetze`)
      .then(r => setSaetze(r.data))
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
        setFehler(`Steuersätze konnten nicht geladen werden: ${msg}`);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const anfrage = async (fn: () => Promise<unknown>) => {
    setFehler(null);
    setSaving(true);
    try {
      await fn();
      load();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
      setFehler(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const prozent = parseFloat(form.prozent.replace(',', '.'));
    if (!Number.isFinite(prozent)) {
      setFehler('Bitte einen gültigen Prozentwert angeben.');
      return;
    }
    anfrage(async () => {
      await axios.post(`${API_URL}/api/steuersaetze`, {
        bezeichnung: form.bezeichnung,
        prozent,
      });
      setForm({ bezeichnung: '', prozent: '' });
    });
  };

  const handleProzentKorrigieren = (s: Steuersatz) => {
    const eingabe = window.prompt(
      `Prozentwert für "${s.bezeichnung}" ändern — verknüpfte Artikel übernehmen den neuen Satz, bestehende Belege behalten ihren eingefrorenen Wert:`,
      String(s.prozent),
    );
    if (eingabe === null) return;
    const wert = parseFloat(eingabe.replace(',', '.'));
    if (!Number.isFinite(wert) || wert < 0 || wert > 100) {
      setFehler('Ungültiger Prozentwert — erlaubt sind 0 bis 100.');
      return;
    }
    anfrage(() => axios.put(`${API_URL}/api/steuersaetze/${s.steuersatz_id}`, { prozent: wert }));
  };

  const handleStandard = (s: Steuersatz) =>
    anfrage(() => axios.put(`${API_URL}/api/steuersaetze/${s.steuersatz_id}`, { ist_standard: true }));

  const handleAktivToggle = (s: Steuersatz) =>
    anfrage(() => axios.put(`${API_URL}/api/steuersaetze/${s.steuersatz_id}`, { aktiv: !s.aktiv }));

  const handleLoeschen = (s: Steuersatz) => {
    if (!window.confirm(`Steuersatz "${s.bezeichnung}" (${s.prozent} %) löschen?`)) return;
    anfrage(() => axios.delete(`${API_URL}/api/steuersaetze/${s.steuersatz_id}`));
  };

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Steuersätze</h3>
      <p style={{ color: 'var(--ink-2)', marginBottom: 16, fontSize: 14 }}>
        Stammdaten für die Umsatzsteuer. Artikel werden mit einem Satz verknüpft;
        Belege frieren den Satz zum Belegzeitpunkt ein.
      </p>

      {fehler && (
        <div style={{ color: 'var(--red)', border: '1px solid var(--red)', padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
          {fehler}
        </div>
      )}

      <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, marginBottom: 18, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Bezeichnung
          <input
            value={form.bezeichnung}
            onChange={e => setForm(f => ({ ...f, bezeichnung: e.target.value }))}
            placeholder="z.B. Ermäßigter Satz"
            required
            style={{ padding: '7px 10px', minWidth: 200 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Prozent
          <input
            value={form.prozent}
            onChange={e => setForm(f => ({ ...f, prozent: e.target.value }))}
            placeholder="7"
            required
            inputMode="decimal"
            style={{ padding: '7px 10px', width: 90 }}
          />
        </label>
        <button type="submit" className="k-btn k-btn--primary" disabled={saving}>
          + Anlegen
        </button>
      </form>

      {loading ? (
        <div>Lade Steuersätze…</div>
      ) : (
        <div className="table-container">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 10px 6px 0' }}>Bezeichnung</th>
                <th style={{ textAlign: 'right', padding: '6px 10px' }}>Prozent</th>
                <th style={{ textAlign: 'center', padding: '6px 10px' }}>Standard</th>
                <th style={{ textAlign: 'center', padding: '6px 10px' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '6px 0' }}></th>
              </tr>
            </thead>
            <tbody>
              {saetze.map(s => (
                <tr key={s.steuersatz_id} style={{ borderBottom: '1px solid var(--bone-3)', opacity: s.aktiv ? 1 : 0.55 }}>
                  <td style={{ padding: '8px 10px 8px 0' }}>{s.bezeichnung}</td>
                  <td className="is-num" style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {s.prozent.toLocaleString('de-DE')} %
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    {s.ist_standard ? (
                      <strong>Standard</strong>
                    ) : (
                      <button type="button" className="k-btn k-btn--sm k-btn--ghost" disabled={saving} onClick={() => handleStandard(s)}>
                        Als Standard
                      </button>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    <button type="button" className="k-btn k-btn--sm k-btn--ghost" disabled={saving} onClick={() => handleAktivToggle(s)}>
                      {s.aktiv ? 'Aktiv' : 'Inaktiv'}
                    </button>
                  </td>
                  <td style={{ padding: '8px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button type="button" className="k-btn k-btn--sm k-btn--ghost" disabled={saving} onClick={() => handleProzentKorrigieren(s)} style={{ marginRight: 6 }}>
                      Prozent ändern
                    </button>
                    <button type="button" className="k-btn k-btn--sm k-btn--danger" disabled={saving} onClick={() => handleLoeschen(s)}>
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SteuersatzSection;
